/** @jsxImportSource @emotion/react */
import {
  EuiButtonEmpty,
  EuiForm,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import {
  DataTableColumnDefinition,
  DataTableColumnGroupDefinition,
} from "../store/DataTableProvider.types";
import { css } from "@emotion/react";
import { FieldColumnType } from "./fields/FieldColumnType";
import { useCallback, useState } from "react";
import { PanelCreateColumnBoolean } from "./ModalCreateColumn/panels/PanelCreateColumnBoolean";
import { PanelCreateColumnFile } from "./ModalCreateColumn/panels/PanelCreateColumnFile";
import { PanelCreateColumnFileMetadatum } from "./ModalCreateColumn/panels/PanelCreateColumnFileMetadatum";
import { PanelCreateColumnFloat } from "./ModalCreateColumn/panels/PanelCreateColumnFloat";
import { PanelCreateColumnIdentifier } from "./ModalCreateColumn/panels/PanelCreateColumnIdentifier";
import { PanelCreateColumnInteger } from "./ModalCreateColumn/panels/PanelCreateColumnInteger";
import { PanelCreateColumnJoin } from "./ModalCreateColumn/panels/PanelCreateColumnJoin";
import { PanelCreateColumnText } from "./ModalCreateColumn/panels/PanelCreateColumnText";
import assert from "assert";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useDataTableContext } from "../store/DataTableProvider";
import { FieldColumnName } from "./fields/FieldColumnName";
import { PanelCreateColumnChoice } from "./ModalCreateColumn/panels/PanelCreateColumnChoice";
import { PanelCreateColumnRoiFrame } from "./ModalCreateColumn/panels/PanelCreateColumnRoiFrame";
import { PanelCreateColumnCellStatus } from "./ModalCreateColumn/panels/PanelCreateColumnCellStatus";
import { ButtonPermissioned } from "components/ButtonPermissioned/ButtonPermissioned";

type Fields = {
  name: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  default_formula: {
    value: string | undefined;
    isValid: boolean;
  };
  definition: {
    value: DataTableColumnDefinition | undefined;
    isValid: boolean;
  };
};

interface ModalEditColumnProps {
  id: string;
  name: string;
  helpText: string | null;
  isDefinitionEditable: boolean;
  definition: DataTableColumnDefinition;
  groupDefinition: DataTableColumnGroupDefinition | undefined;
  defaultFormula: string | null;
  isIdColumn: boolean;
  tableId: string;
  onClose: () => void;
}

export const ModalEditColumn = ({
  id,
  name,
  helpText,
  isDefinitionEditable,
  definition,
  groupDefinition,
  defaultFormula,
  isIdColumn,
  tableId,
  onClose,
}: ModalEditColumnProps) => {
  const updateColumn = useDataTableContext((s) => s.updateColumn);
  const [isLoading, setIsLoading] = useState(false);
  const [formId] = useState(htmlIdGenerator()());
  const [fields, setFields] = useState<Fields>({
    name: {
      value: name,
      isValid: true,
      isDirty: true,
    },
    default_formula: {
      value: defaultFormula ?? undefined,
      isValid: true,
    },
    definition: {
      value: definition,
      isValid: true,
    },
  });

  const handleFieldChange = useCallback(
    <T extends keyof Fields>(fieldName: T, field: Fields[T]) => {
      setFields((prevFields) => ({
        ...prevFields,
        [fieldName]: field,
      }));
    },
    [],
  );

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      assert(fields.definition.value !== undefined);
      const { error } = await updateColumn({
        tableId,
        columnId: id,
        name: fields.name.value,
        defaultFormula: fields.default_formula.value ?? null,
        definition: isDefinitionEditable ? fields.definition.value : undefined,
      });
      assert(error === undefined);
      addUtilityToastSuccess("Column updated");
    } catch (error) {
      addUtilityToastFailure("Failed to update column");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <EuiModal
      onClose={onClose}
      css={css`
        width: 400px;
      `}
    >
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">{name}</EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiForm
        id={formId}
        component="form"
        role="form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <EuiModalBody>
          {isIdColumn && (
            <>
              <EuiText size="s">
                <p>This column stores the table&apos;s row identifiers.</p>
              </EuiText>
              <EuiSpacer />
            </>
          )}

          {helpText !== null && helpText !== "" && (
            <>
              <EuiText size="s">
                <p>{helpText}</p>
              </EuiText>
              <EuiSpacer />
            </>
          )}

          <FieldColumnType
            autoFocus
            readOnly
            value={definition.kind}
            onChange={() => undefined}
          />

          {(() => {
            if (isIdColumn) {
              return (
                <FieldColumnName
                  autoFocus={false}
                  value={fields.name.value}
                  onChange={(field) => {
                    handleFieldChange("name", {
                      value: field.value,
                      isValid: field.isValid,
                      isDirty: true,
                    });
                  }}
                />
              );
            }

            switch (definition.kind) {
              case "boolean":
                return (
                  <PanelCreateColumnBoolean
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "cell_status":
                return (
                  <PanelCreateColumnCellStatus
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "choice":
                return (
                  <PanelCreateColumnChoice
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "file":
                return (
                  <PanelCreateColumnFile
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                    singleSelection={isDefinitionEditable}
                  />
                );
              case "metadatum":
                return (
                  <PanelCreateColumnFileMetadatum
                    tableId={tableId}
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "float":
                return (
                  <PanelCreateColumnFloat
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "identifier":
                return (
                  <PanelCreateColumnIdentifier
                    autoFocus={false}
                    isDefinitionEditable={
                      isDefinitionEditable && groupDefinition?.kind !== "join"
                    }
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "integer":
                return (
                  <PanelCreateColumnInteger
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "join":
                return (
                  <PanelCreateColumnJoin
                    autoFocus={false}
                    isDefinitionEditable={
                      isDefinitionEditable && groupDefinition?.kind !== "join"
                    }
                    fields={fields}
                    onFieldChange={handleFieldChange}
                    sourceColumnTableId={
                      groupDefinition?.kind === "join"
                        ? groupDefinition.table.id
                        : undefined
                    }
                    tableId={tableId}
                  />
                );
              case "roi_frame":
                return (
                  <PanelCreateColumnRoiFrame
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "text":
                return (
                  <PanelCreateColumnText
                    autoFocus={false}
                    isDefinitionEditable={isDefinitionEditable}
                    fields={fields}
                    onFieldChange={handleFieldChange}
                  />
                );
              case "date":
                return (
                  <FieldColumnName
                    autoFocus={true}
                    value={fields.name.value}
                    onChange={(field) => {
                      handleFieldChange("name", {
                        value: field.value,
                        isValid: field.isValid,
                        isDirty: true,
                      });
                    }}
                  />
                );
              case undefined:
                return null;
            }
          })()}
        </EuiModalBody>
      </EuiForm>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
        <ButtonPermissioned
          requiredPermission="edit"
          type="submit"
          form={formId}
          isLoading={isLoading}
          fill
          disabled={Object.values(fields).some((field) => !field.isValid)}
        >
          Update
        </ButtonPermissioned>
      </EuiModalFooter>
    </EuiModal>
  );
};
